import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

/**
 * Log da instalação em tempo real.
 *
 * Consome SSE. O importante aqui é o operador ver progresso: instalar um nó
 * leva minutos, e uma tela parada com um spinner faz qualquer pessoa achar que
 * travou e reiniciar no meio — o que deixaria o nó pela metade.
 */

interface LogProps {
  jobId: string;
  aoTerminar?: (status: 'OK' | 'FALHA') => void;
}

/** Traduz as sequências ANSI que os scripts emitem para classes de cor. */
function pintar(linha: string): { texto: string; classe: string } {
  const limpa = linha.replace(/\x1b\[[0-9;]*m/g, '');

  if (/\[1;31m|\[erro\]/.test(linha)) return { texto: limpa, classe: 'text-[#ff8a80]' };
  if (/\[1;33m|\[aviso\]/.test(linha)) return { texto: limpa, classe: 'text-[#ffd180]' };
  if (/\[1;32m/.test(linha)) return { texto: limpa, classe: 'text-[#a5d6a7] font-bold' };
  if (/\[1;34m|^==>/.test(linha)) return { texto: limpa, classe: 'text-[#82b1ff] font-bold' };
  return { texto: limpa, classe: 'text-[#cfd8dc]' };
}

export function LogProvisionamento({ jobId, aoTerminar }: LogProps) {
  const [linhas, setLinhas] = useState<string[]>([]);
  const [estado, setEstado] = useState<'rodando' | 'OK' | 'FALHA'>('rodando');
  const fim = useRef<HTMLDivElement>(null);
  const [seguirFim, setSeguirFim] = useState(true);

  useEffect(() => {
    const fonte = new EventSource(`/api/infra/nos/jobs/${jobId}/log`);

    fonte.onmessage = (e) => {
      const dado = JSON.parse(e.data as string) as
        | { tipo: 'linha'; linha: string }
        | { tipo: 'fim'; status: 'OK' | 'FALHA' };

      if (dado.tipo === 'linha') {
        setLinhas((l) => [...l, dado.linha]);
      } else {
        setEstado(dado.status);
        aoTerminar?.(dado.status);
        fonte.close();
      }
    };

    // O EventSource reconecta sozinho, mas depois que o job terminou não há
    // mais nada para receber — fechar evita uma reconexão a cada 3 segundos.
    fonte.onerror = () => {
      if (estado !== 'rodando') fonte.close();
    };

    return () => fonte.close();
  }, [jobId, aoTerminar, estado]);

  // Rola sozinho, mas só enquanto o operador não subiu para ler algo. Rolar à
  // força enquanto alguém investiga uma linha de erro é irritante.
  useEffect(() => {
    if (seguirFim) fim.current?.scrollIntoView({ behavior: 'smooth' });
  }, [linhas, seguirFim]);

  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-[13px] font-bold">
        {estado === 'rodando' && (
          <>
            <Loader2 size={15} className="animate-spin text-marca" />
            <span className="text-marca">Instalando…</span>
          </>
        )}
        {estado === 'OK' && (
          <>
            <CheckCircle2 size={15} className="text-sucesso" />
            <span className="text-sucesso">Nó provisionado com sucesso</span>
          </>
        )}
        {estado === 'FALHA' && (
          <>
            <XCircle size={15} className="text-erro" />
            <span className="text-erro">A instalação falhou</span>
          </>
        )}
        <span className="ml-auto text-[11.5px] font-semibold text-texto-mudo">
          {linhas.length} linhas
        </span>
      </div>

      <div
        onScroll={(e) => {
          const el = e.currentTarget;
          setSeguirFim(el.scrollHeight - el.scrollTop - el.clientHeight < 40);
        }}
        className="h-[340px] overflow-y-auto rounded-chip bg-[#0f2846] p-3 font-mono text-[11.5px] leading-relaxed"
      >
        {linhas.length === 0 ? (
          <p className="text-[#607d8b]">Aguardando saída do servidor…</p>
        ) : (
          linhas.map((l, i) => {
            const { texto, classe } = pintar(l);
            return (
              <div key={i} className={`whitespace-pre-wrap break-all ${classe}`}>
                {texto || ' '}
              </div>
            );
          })
        )}
        <div ref={fim} />
      </div>

      {!seguirFim && (
        <button
          onClick={() => setSeguirFim(true)}
          className="mt-2 text-[12px] font-semibold text-marca hover:underline"
        >
          voltar para o fim do log
        </button>
      )}
    </div>
  );
}
